export default {
  title: {
    id: 'rm_mine-my-phone-story11_name',
    defaultMessage:
      'In Bolivia, teenagers are quitting school to mine in dangerous conditions',
  },
  excerpt: {
    id: 'rm_mine-my-phone-story11_excerpt',
    defaultMessage:
      'One of the world’s richest sources of indium has not received a cent for the critical metal extracted from its land, some of it produced with child labor, as minors seek income amid the country’s worsening economic crisis.',
  },
  regionName: {
    id: 'rm_mine-my-phone-story11_region_name',
    defaultMessage: 'Potosí',
  },
  s1P1: {
    id: 'rm_mine-my-phone-story11_s1_p1',
    defaultMessage:
      'In Bolivia’s southern highland mines of Potosí, a critical metal used in smartphones is extracted every day in unsafe conditions. These include the employment and exploitation of minors, all just trying to earn a living in a crumbling economy.',
  },
  s1P2: {
    id: 'rm_mine-my-phone-story11_s1_p2',
    defaultMessage:
      'It’s a story that spans all the way back to colonial times, an era when [tons of silver were extracted from this region](https://www.theguardian.com/cities/2016/mar/21/story-of-cities-6-potosi-bolivia-peru-inca-first-city-capitalism) to fill the coffers of the Spanish Empire. Today, hundreds of years later, international companies still extract valuable raw materials and metals like zinc ore despite Bolivia not receiving a single cent for them. Meanwhile, small-scale miners keep digging with picks and shovels, unaware of the value shipped abroad to be used in popular technological devices found around the world.',
  },
  s1P3: {
    id: 'rm_mine-my-phone-story11_s1_p3',
    defaultMessage:
      'Jonathan is 14 and works at a mine in Potosí during school vacations. He started last year after a relative invited him to earn money to help support his family. What he remembers most from his first days underground is the darkness: deep and absolute, broken only by the faint beam of light from his helmet lamp. His father died, and his mother also began working at the mine, collecting ore brought out of the tunnels.',
  },
  s2P1: {
    id: 'rm_mine-my-phone-story11_s2_p1',
    defaultMessage:
      '“When I go on vacation, I go to the mine. My mom works there too. She’s a llampera at the mine. The llamperas sort the ore after the carts dump the material,” Jonathan explained.',
  },
  s2P2: {
    id: 'rm_mine-my-phone-story11_s2_p2',
    defaultMessage:
      'He is in his second year of high school and may return to the mine during the next vacation, along with two of his classmates. They have no idea that the zinc ore they extracted from these arid lands is refined in European and Asian countries to recover indium, a critical metal in an increasingly technological world that is used to manufacture touchscreen cell phones, flat-screen displays, LED lights and solar panels.',
  },
  s2P3: {
    id: 'rm_mine-my-phone-story11_s2_p3',
    defaultMessage:
      'The zinc-bearing ore extracted from the mines is hauled to concentrator plants, where it is then crushed and processed through froth flotation to produce a zinc concentrate. The concentrate is then shipped through Chilean seaports to zinc smelters, where indium and other trace metals become concentrated in processing residues.',
  },
  s2P4: {
    id: 'rm_mine-my-phone-story11_s2_p4',
    defaultMessage:
      'Those residues are later sent to specialized refineries, where the indium is finally recovered. Thanks to its unique combination of electrical conductivity and transparency, indium is used to produce the ultra-thin conductive coating that allows smartphone touchscreens to respond to the tiny electrical signals transmitted by our fingertips.',
  },
  s3Title: {
    id: 'rm_mine-my-phone-story11_s3_title',
    defaultMessage: 'An indium economy',
  },
  s3P1: {
    id: 'rm_mine-my-phone-story11_s3_p1',
    defaultMessage:
      'Bolivia is an important global source of unrefined indium, with its zinc ores containing some of the world’s highest concentrations of the metal. The department of Potosí accounts for 86% of the country’s zinc production, [according to Mining Ministry](https://www.mineria.gob.bo/wp-content/uploads/2026/04/Anuario-2025.pdf) data. It is also Bolivia’s largest exporting department, [according to the National Statistics Institute](https://www.ine.gob.bo/index.php/estadisticas-economicas/comercio-exterior/cuadros-estadisticos-exportaciones/). Ironically, though, Potosí is one of the country’s [poorest](https://jubileobolivia.com/Publicaciones/Revistas-especializadas/Cerca-de-6-millones-de-bolivianos-viven-en-pobreza-y-sectores-de-clase-media-vulnerable-podrian-aumentar-esta-cifra) departments, with thousands of people lacking enough income to meet minimum daily nutritional needs.',
  },
  s3P2: {
    id: 'rm_mine-my-phone-story11_s3_p2',
    defaultMessage:
      'Indium produced with child labor usually originates in cooperatives and small-scale informal mining, a sector that has gained considerable influence and economic power during Bolivia’s socialist era under the MAS party. This administration, led by left-wing leader Evo Morales, remained in power for nearly 20 consecutive years, stretching constitutional term limits, and pursued a primarily extractive economic model that relied heavily on hydrocarbons and mineral exploitation.',
  },
  s3P3: {
    id: 'rm_mine-my-phone-story11_s3_p3',
    defaultMessage:
      'Although only 9% of zinc production officially comes from cooperatives, according to government [data](https://www.mineria.gob.bo/wp-content/uploads/2026/04/Anuario-2025.pdf), those figures may also be misleading. This is because production reports rely on good-faith declarations, adulterated paperwork, and the absence of a reliable traceability system. Effective traceability is not enforced. Officials rely on “good faith” declarations to register the origin of minerals without conducting rigorous inspections, explains Alfredo Zaconeta, a researcher at Bolivian think tank, Centro de Estudios para el Desarrollo Laboral y Agrario (CEDLA).',
  },
  s4Title: {
    id: 'rm_mine-my-phone-story11_s4_title',
    defaultMessage: 'So, where does the indium go?',
  },
  s4P1: {
    id: 'rm_mine-my-phone-story11_s4_p1',
    defaultMessage:
      'Around 67% of Bolivia’s zinc exports go to Asia: 33.6% to Japan, 23.9% to China, and 9.5% to South Korea, according to Mining Ministry data. Those three countries are among the world’s leading refined indium producers. Together, they account for around [93% of global refined indium](https://pubs.usgs.gov/periodicals/mcs2025/mcs2025-indium.pdf) production. However, many other countries are part of the global indium supply chain.',
  },
  s4P2: {
    id: 'rm_mine-my-phone-story11_s4_p2',
    defaultMessage:
      'The U.S. Department of Labor added Bolivian zinc to its list of goods produced with child labor in 2010. And, in 2024, it was [reported](https://www.dol.gov/sites/dolgov/files/ILAB/child_labor_reports/tda2023/2024-tvpra-list-of-goods.pdf) that indium production in South Korea relied on Bolivian zinc extracted with child labor. These are first-world countries that position themselves as responsible leaders in the global economy, which makes the use of child labor an obvious contradiction.',
  },
  s5P1: {
    id: 'rm_mine-my-phone-story11_s5_p1',
    defaultMessage:
      'Bolivia’s Mining Ministry didn’t respond to requests for comment. None of the embassies of the countries mentioned responded to requests for comment, either, and Glencore, another company that has been [accused by](https://globalarbitrationreview.com/article/bolivia-fails-set-aside-glencore-award-over-alleged-illegality) the press of trading Bolivian zinc, did not give comment. A spokesperson for Trafigura explained that the company does not trade indium directly, although it “purchases zinc concentrates from Bolivia, subject to our Responsible Sourcing Programme, which includes supplier screening and third-party audits against human rights criteria, including child labor risk.”',
  },
  s5P2: {
    id: 'rm_mine-my-phone-story11_s5_p2',
    defaultMessage:
      'The spokesperson added: “Where gaps are identified, suppliers must implement improvement plans; where risks cannot be mitigated, we implement a structured responsible disengagement procedure.”',
  },
  s6Title: {
    id: 'rm_mine-my-phone-story11_s6_title',
    defaultMessage: 'The normalisation of underage miners',
  },
  s6P1: {
    id: 'rm_mine-my-phone-story11_s6_p1',
    defaultMessage:
      'Although children under 14 are not commonly found in Bolivian mines, teenagers above that age still are, according to multiple sources familiar with the issue. They are typically employed to push wheelbarrows, work in narrow tunnels, warn of explosions, and search for minerals in discarded soil.',
  },
  s6P2: {
    id: 'rm_mine-my-phone-story11_s6_p2',
    defaultMessage:
      '“During school vacations, the mines fill up with underage workers. Nobody asks for an ID or anything,” said a 20-year-old miner, who asked not to be identified due to fears of losing his job. He started working in the mines at 16 and has continued there ever since. His three brothers and his mother also work in mining.',
  },
  s7P1: {
    id: 'rm_mine-my-phone-story11_s7_p1',
    defaultMessage:
      'Minors are exposed to potential tunnel collapses, toxic fumes, and dust. Like all miners employed by cooperatives, work is conducted without adequate safety measures in an environment where alcohol consumption is also widespread. Although cooperatives were originally conceived as a way for poor miners to organize and create their own employment, in practice, “they have become private companies”, claimed Vivian Lagrava, a researcher at local human rights organization Empodérate.',
  },
  s7P2: {
    id: 'rm_mine-my-phone-story11_s7_p2',
    defaultMessage:
      'In some cases, cooperative members do not work themselves and instead collect dividends from mineral extraction while hiring third parties to do the work; a practice considered illegal. Cooperatives do not pay taxes, pay low royalties, and hire workers without legal benefits (including teenagers) Lagrava explained. “The cooperatives have distorted their original purpose to increase the economic benefits of their members.”',
  },
  s8Title: {
    id: 'rm_mine-my-phone-story11_s8_title',
    defaultMessage: 'The teenage cost of indium',
  },
  s8P1: {
    id: 'rm_mine-my-phone-story11_s8_p1',
    defaultMessage:
      'The recent rise in mineral prices has made mining more lucrative, with miners earning around 300 bolivianos (the equivalent to $25 a day in August 2026), nearly three times the monthly minimum wage if work is consistent. Teenagers are not necessarily forced into mining; in many cases, they choose it because of economic necessity, Zaconeta said.',
  },
  s8P2: {
    id: 'rm_mine-my-phone-story11_s8_p2',
    defaultMessage:
      'Bolivia’s recent economic crisis, marked by rampant inflation, political instability and a lack of jobs after 20 years of economic mismanagement under socialist governments, has pushed minors to help put food on the table. They are considered “cheap labor” and are usually paid less than adult miners because cooperative members argue they risk sanctions from employing underage workers, explained Esdenka Mullerina, a former member of CONNATSOP (Consejo de Niños, Niñas y Adolescentes Trabajadores Organizados de Potosí). This is an organization representing underage workers in Potosí.',
  },
  s8P3: {
    id: 'rm_mine-my-phone-story11_s8_p3',
    defaultMessage:
      'Bolivian [law](https://www.coordinadoradelamujer.org.bo/observatorio/archivos/marco/l548_389.pdf)s allow teenagers to work from the age of 14, but it also prohibits hazardous activities such as mining. The situation is widely known and acknowledged by cooperative leaders. “That’s true, I cannot deny it. I cannot control all the cooperative members. We are millions,” Óscar Chavarría, head of the Cooperative Miners Federation of Potosí, said.',
  },
  s8P4: {
    id: 'rm_mine-my-phone-story11_s8_p4',
    defaultMessage:
      'He added that the federation works to eliminate child labor among its affiliates, although minors themselves often seek employment. “Many have come to my office saying they need to work.”',
  },
  s8P5: {
    id: 'rm_mine-my-phone-story11_s8_p5',
    defaultMessage:
      'Teenagers frequently abandon school to prioritize work. Others who cannot find jobs in cooperatives join criminal groups that steal minerals, an activity locally known as jukeo. The preference for quick income over education, health and long-term development reflects broader regional and national pressures. It also shows how Fast Tech tends to forget about the miners making its mechanics possible.',
  },
  s9Title: {
    id: 'rm_mine-my-phone-story11_s9_title',
    defaultMessage: 'Bolivia’s economic inequality',
  },
  s9P1: {
    id: 'rm_mine-my-phone-story11_s9_p1',
    defaultMessage:
      'Despite Bolivia’s zinc ores containing some of the world’s richest concentrations of indium, the country receives no payment for it. Bolivia actually loses [millions](https://hemeroteca.larazon.bo/economia/2023/01/27/en-la-exportacion-de-zinc-bolivia-regala-us-1-200-millones-en-subproductos/) of dollars every year, simply because technological minerals contained in zinc concentrates are exported without being declared.',
  },
  s9P2: {
    id: 'rm_mine-my-phone-story11_s9_p2',
    defaultMessage:
      '“Indium-rich minerals extracted by mining cooperatives are transported and further processed as a by-product from zinc refining in several countries, but leave no added financial value in Bolivia, both for locals or the state,” a 2020 [study](https://catapa.be/en/linking-the-bolivian-minerals-to-the-european-industry/) by Belgian NGO CATAPA said.',
  },
  s9P3: {
    id: 'rm_mine-my-phone-story11_s9_p3',
    defaultMessage:
      '“Corporations profiting from these minerals should contribute to remediating social and environmental damage, while traders that cannot guarantee the clean origin of the metals they buy and sell should not be able to sell in the international market,” the CATAPA study suggested.',
  },
  s9P4: {
    id: 'rm_mine-my-phone-story11_s9_p4',
    defaultMessage:
      'For decades, authorities have planned to build a zinc refinery to recover those minerals, but the project has never fully materialized. The latest proposal, a $329 million zinc refinery to be built by the Chinese consortium conformed by ENFI Engineering Co. and the China Railway International Group, was [canceled](https://www.bloomberg.com/news/articles/2026-01-28/bolivia-cancels-chinese-zinc-project-demands-steel-plant-fixes) after the administration that replaced two decades of socialist rule took office.',
  },
  s10P1: {
    id: 'rm_mine-my-phone-story11_s10_p1',
    defaultMessage:
      'Back in June, Bolivian officials held talks with South Korea on cooperation in the energy transition, critical minerals, and rare earths as part of President Rodrigo Paz’s plan to open Bolivia’s mineral wealth to foreign investment.',
  },
  s10P2: {
    id: 'rm_mine-my-phone-story11_s10_p2',
    defaultMessage:
      '“Under current circumstances, Bolivia is not in a position to eradicate child labor in mining,” said Luz Rivera, a member of Pastoral Social Caritas Potosí.',
  },
  s10P3: {
    id: 'rm_mine-my-phone-story11_s10_p3',
    defaultMessage:
      'Instead, authorities should strengthen household incomes, diversify employment opportunities, improve labor conditions, and finally raise the quality of education, so children and teenagers have alternatives to spending years in dark mining tunnels as their only chance to escape poverty. “What should be banned is child exploitation, not child labor,” Mullerina controversially claimed.',
  },
  s10P4: {
    id: 'rm_mine-my-phone-story11_s10_p4',
    defaultMessage:
      'She has seen firsthand how harsh life in the mines can be for teenagers, attending the funerals of friends killed in tunnel collapses and witnessing domestic violence. But she has also seen stories that defy common assumptions, with some young miners finding genuine opportunities through work that remains legally prohibited.',
  },
  s10P5: {
    id: 'rm_mine-my-phone-story11_s10_p5',
    defaultMessage:
      'In a single operation during the first Friday of Bolivia’s winter school holidays, the regional Ombudsman’s Office [found](https://www.defensoria.gob.bo/oficinas/prensa/defensoria-del-pueblo-acompanya-operativo-en-porco-que-identifica-a-15-adolescentes-que-pretendian-ingresar-a-trabajar-en-interior-mina?fbclid=IwY2xjawTDaYFleHRuA2FlbQIxMABicmlkETFZQlJEMUhuaXYxNUMyODZmc3J0YwZhcHBfaWQQMjIyMDM5MTc4ODIwMDg5MgABHmnCx0PKpl2vYe9UCMrsz7Am-tqLTAK3uQEIXF0lauJduUm5nhM-6oZaTnoM_aem_s2T2pBo4FBNHIs5c97H4Fw) 15 teenagers under the age of 17 entering the Porco mines in Potosí, including one girl. Bolivia’s mineral exports rose 120% in value through May 2026 from a year earlier, driven largely by higher prices, even as export volumes fell 18%, according to official data. But unless the country’s new right-leaning government undertakes sweeping reforms, Bolivia will continue exporting millions of dollars’ worth of indium without receiving a penny from the metal itself, proof of how the profit share behind Fast Tech and global raw materials extraction continues to be uneven.',
  },
  f1P1: {
    id: 'rm_mine-my-phone-story11_f1_p1',
    defaultMessage:
      'Detail of a young miner’s face in foreground with lamp off and a representation of “El Tio”. Credit: Simone Francesangeli',
  },
  f2P1: {
    id: 'rm_mine-my-phone-story11_f2_p1',
    defaultMessage:
      'A child miner struggles to carry rocks out of a tunnel. Credit: Simone Francesangeli',
  },
  f3P1: {
    id: 'rm_mine-my-phone-story11_f3_p1',
    defaultMessage:
      'A young miner about to leave on his motorbike. Credit: Simone Francesangeli',
  },
  f4P1: {
    id: 'rm_mine-my-phone-story11_f4_p1',
    defaultMessage:
      'A child miner breaks rocks in a tunnel in Cerro Rico, Potosí, Bolivia. Credit: Simone Francesangeli',
  },
  f5P1: {
    id: 'rm_mine-my-phone-story11_f5_p1',
    defaultMessage:
      'A father and his young son, both miners, preparing dynamite for mine explosions. Credit: Simone Francesangeli',
  },
  f6P1: {
    id: 'rm_mine-my-phone-story11_f6_p1',
    defaultMessage:
      'A young miner with his young partner, expecting a child. Credit: Simone Francesangeli',
  },
  f7P1: {
    id: 'rm_mine-my-phone-story11_f7_p1',
    defaultMessage:
      'Detail of a young miner and his father, taking a break from hard work inside a tunnel. Credit: Simone Francesangeli',
  },
  q1: {
    id: 'rm_mine-my-phone-story11_q1',
    defaultMessage:
      '“During school vacations, the mines fill up with underage workers. Nobody asks for an ID or anything.”',
  },
  q1Attr: {
    id: 'rm_mine-my-phone-story11_q1_attr',
    defaultMessage: 'Anon 20-year-old miner',
  },
  q2: {
    id: 'rm_mine-my-phone-story11_q2',
    defaultMessage:
      '“Under current circumstances, Bolivia is not in a position to eradicate child labor in mining.”',
  },
  q2Attr: {
    id: 'rm_mine-my-phone-story11_q2_attr',
    defaultMessage: 'Luz Rivera, a member of Pastoral Social Caritas Potosí',
  },
} as const
